"use client";

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import cn from "classnames";

import { wrapReviewQuote } from "@/components/adtCustomerReviewsSection/adtCustomerReviewsSection.utils";
import { StarRating } from "@/components/starRating/starRating.component";
import { brown } from "@/app/font";

import type { SocialProofQuote } from "./adtSocialProof.types";
import {
  QUOTE_LINE_LIMIT,
  SOCIAL_PROOF_LABELS,
  socialProofEyebrowClass,
} from "./adtSocialProof.utils";
import { AdtSocialProofCarousel } from "./adtSocialProofCarousel.component";

export function AdtSocialProofTestimonials({
  quotes,
  eyebrow,
  seconds,
  previousLabel,
  nextLabel,
}: AdtSocialProofTestimonialsProps) {
  const panelId = useId();
  const { ready, reduced } = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const quote = quotes[index] ?? quotes[0];
  const canRotate = quotes.length > 1;
  const rotationOn = ready && !reduced && !paused && canRotate;

  useEffect(() => {
    if (!rotationOn) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      setIndex((current) => (current + 1) % quotes.length);
    }, seconds * 1000);
    return () => window.clearInterval(id);
  }, [rotationOn, seconds, quotes.length, index]);

  const select = (next: number) => setIndex(next);
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    setExpanded(false);
  }, [quote._key]);

  useLayoutEffect(() => {
    const node = quoteRef.current;
    if (!node || expanded) {
      setOverflows(false);
      return;
    }
    setOverflows(node.scrollHeight > node.clientHeight + 1);
  }, [quote._key, quote.quote, expanded]);

  const rating = quote.rating ?? 5;
  const showSeeMore = overflows && !expanded;

  return (
    <AdtSocialProofCarousel
      count={quotes.length}
      index={index}
      onSelect={select}
      previousLabel={previousLabel}
      nextLabel={nextLabel}
      itemLabel="Testimonial"
      groupLabel="Customer testimonials"
      labelledBy={panelId}
      placement="footer"
      testId="social-proof-testimonials"
      onPointerEnter={(event) => {
        if (event.pointerType === "touch") return;
        setPaused(true);
      }}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) return;
        setPaused(false);
      }}
      className="flex flex-col px-6 py-8 sm:px-8 lg:px-10 lg:py-10">
      {eyebrow ? <p className={socialProofEyebrowClass}>{eyebrow}</p> : null}
      <div
        className="mt-4 flex gap-1"
        role="img"
        aria-label={`${rating} out of 5 stars`}>
        <StarRating
          rating={rating}
          width={20}
          height={20}
          className="fill-adt-amber"
          emptyClassName="fill-white/35"
        />
      </div>
      <div id={panelId} aria-live="polite" aria-atomic="true" className="mt-4">
        <p
          ref={quoteRef}
          className={cn(
            brown.className,
            "text-[20px] leading-[1.35] text-white",
            expanded
              ? undefined
              : "line-clamp-6 [display:-webkit-box] overflow-hidden [-webkit-box-orient:vertical]",
          )}
          style={expanded ? undefined : { WebkitLineClamp: QUOTE_LINE_LIMIT }}>
          {wrapReviewQuote(quote.quote)}
        </p>
        {showSeeMore ? (
          <button
            type="button"
            aria-expanded={false}
            onClick={() => setExpanded(true)}
            className="mt-4 inline-flex text-base text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
            {SOCIAL_PROOF_LABELS.seeMore}
          </button>
        ) : null}
        <p className="mt-6 text-sm text-white">
          {quote.name}
          {quote.attribution ? ` · ${quote.attribution}` : null}
        </p>
        {quote.location ? (
          <p className="text-sm text-white">{quote.location}</p>
        ) : null}
      </div>
    </AdtSocialProofCarousel>
  );
}

function useReducedMotion() {
  const [state, setState] = useState({ ready: false, reduced: false });

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      setState({ ready: true, reduced: false });
      return;
    }
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setState({ ready: true, reduced: media.matches });
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return state;
}

type AdtSocialProofTestimonialsProps = {
  quotes: SocialProofQuote[];
  eyebrow?: string | null;
  seconds: number;
  previousLabel: string;
  nextLabel: string;
};
